import { ReactNode } from 'react';

import {
  Card,
  CardContent,
  CardProps,
  Divider,
  LinearProgress,
  Typography,
  makeStyles,
} from '@material-ui/core';

import { MeasurementConfig } from '../../helpers/time-window';
import { SimplifiedQuery } from './SimplifiedQuery';
import {
  MeasurementInput,
  isAverageMeasurement,
  isRatioMeasurement,
} from './snippets';

const useStyles = makeStyles(theme => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
  },
  headerText: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  actions: {
    flexShrink: 0,
  },
}));

export type QueryPreviewCardProps = CardProps & {
  entity?: string | null;
  factTable?: string | null;
  measurement?: MeasurementInput;
  metricWindow?: MeasurementConfig;
  dimMeasurement?: boolean;
  title?: string;
  description?: string;
  loading?: boolean;
  empty?: ReactNode;
  actions?: ReactNode;
};

export const QueryPreviewCard = ({
  entity,
  factTable,
  measurement,
  metricWindow,
  dimMeasurement,
  title,
  description,
  loading,
  empty,
  actions,
  ...rest
}: QueryPreviewCardProps) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Card {...rest}>
        <CardContent>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  const hasQuery =
    measurement &&
    (isAverageMeasurement(measurement) || isRatioMeasurement(measurement));

  if (!hasQuery) {
    if (empty) {
      return (
        <Card {...rest}>
          <CardContent>{empty}</CardContent>
        </Card>
      );
    }
    return null;
  }

  const hasHeader = !!(title || description || actions);

  return (
    <Card {...rest}>
      <CardContent className={classes.content}>
        {hasHeader && (
          <>
            <div className={classes.header}>
              <div className={classes.headerText}>
                {title && <Typography variant="h6">{title}</Typography>}
                {description && (
                  <Typography variant="body2" color="textSecondary">
                    {description}
                  </Typography>
                )}
              </div>
              {actions && <div className={classes.actions}>{actions}</div>}
            </div>
            <Divider />
          </>
        )}
        <SimplifiedQuery
          entity={entity}
          factTable={factTable}
          measurement={measurement}
          metricWindow={metricWindow}
          dimMeasurement={dimMeasurement}
        />
      </CardContent>
    </Card>
  );
};
